import {
  createTheme,
  type PaletteOptions,
  type Theme
} from '@mui/material/styles'
import { customStyleDx } from './customStyleDx'
import { defaultStyleDx } from './defaultStyleDx'
import dxIconsTtf from './fonts/DxFontIcons/dxiconsmaterial.ttf'
import dxIconsWoff from './fonts/DxFontIcons/dxiconsmaterial.woff'
import dxIconsWoff2 from './fonts/DxFontIcons/dxiconsmaterial.woff2'
import manropeBoldFont from './fonts/Manrope/Manrope-Bold.ttf'
import manropeExtraBoldFont from './fonts/Manrope/Manrope-ExtraBold.ttf'
import manropeExtraLightFont from './fonts/Manrope/Manrope-ExtraLight.ttf'
import manropeLightFont from './fonts/Manrope/Manrope-Light.ttf'
import manropeMedFont from './fonts/Manrope/Manrope-Medium.ttf'
import manropeRegFont from './fonts/Manrope/Manrope-Regular.ttf'
import manropeSemiBoldFont from './fonts/Manrope/Manrope-SemiBold.ttf'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    error: React.CSSProperties
    accent: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    error?: React.CSSProperties
    accent?: React.CSSProperties
  }

  interface SimplePaletteColorOptions extends PaletteOptions {
    lightest?: string
    darkest?: string
    disabled?: string
    error?: string
    success?: string
    errorLight?: string
    errorLightest?: string
    errorDark?: string
    errorDarkDisabled?: string
    errorDisabled?: string
  }
  interface PaletteColor {
    mainDisabled?: string
    opaque?: string
    mainHover?: string
    darkOpaque?: string
    lavenderBlush?: string
    darkMain?: string
    purpleLight?: string
    purpleLighter?: string
    purpleMain?: string
    purpleOpaque?: string
    warning?: string
    warningLight?: string
    melon?: string
    0?: string
    50?: string
    100?: string
    200?: string
    300?: string
    400?: string
    500?: string
    600?: string
    700?: string
    900?: string
    lightest?: string
    lightBlue?: string
    lightPeach?: string
    lightGray?: string
    darkBlue?: string
    blue?: string
    extralight?: string
    darkest?: string
    disabled?: string
    error?: string
    errorLightest?: string
    errorLight?: string
    errorDark?: string
    errorDarkDisabled?: string
    success?: string
    errorDisabled?: string
    warningBannerBg?: string
    errorBannerBg?: string
    warningContent?: string
    errorContent?: string
    successContentPrimary?: string
    successContentSecondary?: string
    surfaceErrorSecondary?: string
    surfaceSuccess?: string
    vividOrange?: string
    successBorderSecondary?: string
    errorContentSecondary?: string
    errorBorderSecondary?: string
    warningBorderSecondary?: string
    mainTertiary?: string
    contentBrand?: string
    lightShadow?: string
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    error: true
    accent: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    linkButton: true
  }
}

export const colors = {
  primary: {
    main: '#00B2A9',
    mainTertiary: '#F0F9F9',
    light: '#59CDC7',
    disabled: 'rgba(89, 205, 199, 0.4)',
    dark: '#00B2A9',
    lightest: '#D4EEED',
    extralight: '#EDF7ED',
    darkest: '#006863',
    success: '#40C057',
    error: '#DC0C00',
    errorLightest: 'rgba(220, 12, 0, 0.1);',
    errorLight: 'rgba(220, 12, 0, 0.40);',
    errorDark: '#AC0A01',
    errorDarkDisabled: 'rgba(172, 10, 1, 0.4)',
    errorDisabled: 'rgba(220, 12, 0, 0.4)',
    warningBannerBg: '#fdf0e6',
    errorBannerBg: '#feeceb',
    warningContent: '#5f2b01',
    errorContent: '#621b16',
    successContentSecondary: '#194D23',
    successContentPrimary: '#40C057',
    successBorderSecondary: '#84D793',
    warningBorderSecondary: '#F3A168',
    errorContentSecondary: '#700600',
    errorBorderSecondary: '#FF736B',
    contentBrand: '#00938B',
    purpleMain: '#5F39CC',
    purpleLighter: '#785ac8ff',
    lightShadow: 'rgba(0, 0, 0, 0.15)'
  },
  secondary: {
    main: '#323232',
    mainHover: '#F2F2F2',
    mainDisabled: 'rgba(50, 50, 50, 0.4)',
    opaque: 'rgba(255, 255, 255, 0.4)',
    darkOpaque: 'rgba(0, 0, 0, 0.4)',
    blue: '#2196F3',
    lightBlue: '#E9F5FE',
    lightPeach: '#FDF1E8',
    lightGray: '#BFC3C2',
    darkBlue: '#0D3C61',
    warning: '#ED7018',
    warningLight: 'rgba(237, 112, 24, 0.05)',
    lavenderBlush: '#FEECEB',
    darkMain: '#621b16',
    purpleLight: '#E1D7F9',
    purpleMain: '#7B61FF',
    purpleOpaque: 'rgba(225, 215, 249, 0.4)',
    melon: '#FFB7B2',
    surfaceErrorSecondary: '#FEF6F6',
    surfaceSuccess: '#E8F7EB',
    vividOrange: '#ED6C02',
    0: 'transparent',
    50: '#FFFFFF',
    100: '#F8F8F8',
    200: '#F2F2F2',
    300: '#D7D7D7',
    400: '#BFC2C3',
    500: '#71777A',
    600: '#545E63',
    700: '#222222',
    900: '#000000'
  }
}

const molsonTheme = createTheme({
  palette: {
    ...colors
  },
  typography: {
    fontFamily: 'Manrope, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    subtitle1: {
      color: colors.secondary[700],
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '24px'
    },
    subtitle2: {
      color: colors.secondary[700],
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '18px'
    },
    h1: {
      color: colors.secondary[900],
      fontWeight: 800,
      fontSize: '1.875rem',
      lineHeight: '50px'
    },
    h2: {
      color: colors.secondary[900],
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: '34px'
    },
    h3: {
      color: colors.secondary[900],
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: '28px'
    },
    h4: {
      color: colors.secondary[900],
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: '20px'
    },
    body1: {
      color: colors.secondary[700],
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '24px'
    },
    error: {
      color: colors.primary.error,
      fontSize: '0.75rem',
      fontWeight: 400,
      margin: '3px 14px 0px 0px'
    },
    accent: {
      color: colors.secondary[500],
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: '34px'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Manrope';
          src: url('${''}') format('truetype'), 
          url('${manropeRegFont}') format('truetype'),
          url('${manropeBoldFont}') format('truetype'),
          url('${manropeExtraBoldFont}') format('truetype'),
          url('${manropeExtraLightFont}') format('truetype'),
          url('${manropeLightFont}') format('truetype'),
          url('${manropeSemiBoldFont}') format('truetype'),
          url('${manropeMedFont}') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        @font-face {
          font-family: DXIcons;
          src: url('${dxIconsWoff2}') format('woff2'),
            url('${dxIconsWoff}') format('woff'),
            url('${dxIconsTtf}') format('truetype');
          font-weight: 400;
          font-style: normal;
        }
        ${defaultStyleDx()}
        ${customStyleDx(colors)}
      `
    },
    MuiButton: {
      styleOverrides: {
        root: {
          ':disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto'
          },
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderRadius: 10,
          boxShadow: 'none',
          minWidth: 40,
          maxHeight: 44,
          width: 'fit-content',
          letterSpacing: '0.01rem',
          textTransform: 'none',
          padding: '10px 16px',
          borderStyle: 'solid',
          borderWidth: 1,
          fontSize: '1rem',
          fontWeight: 700,
          lineHeight: '20px'
        },
        startIcon: {
          margin: 0
        },
        endIcon: {
          margin: 0
        },
        outlinedSecondary: ({ theme }) => ({
          borderColor: theme.palette.secondary[700],
          background: theme.palette.secondary[50],
          color: theme.palette.secondary[700],

          '& svg path': {
            stroke: theme.palette.secondary.main,
            fill: theme.palette.secondary.main
          },

          ':hover': {
            borderColor: theme.palette.secondary[900],
            backgroundColor: theme.palette.secondary.mainHover
          },

          ':disabled': {
            '& svg path': {
              stroke: theme.palette.secondary.mainDisabled,
              fill: theme.palette.secondary.mainDisabled
            },
            borderColor: theme.palette.secondary.darkOpaque,
            backgroundColor: theme.palette.secondary.opaque
          }
        }),
        containedPrimary: ({ theme }) => ({
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.secondary[700],

          '& svg path': {
            stroke: theme.palette.secondary.main,
            fill: theme.palette.secondary.main
          },

          ':hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.dark
          },
          ':disabled': {
            '& svg path': {
              stroke: theme.palette.secondary.mainDisabled,
              fill: theme.palette.secondary.mainDisabled
            },
            borderColor: theme.palette.secondary[0],
            backgroundColor: theme.palette.primary.disabled
          }
        }),
        textSecondary: ({ theme }) => ({
          color: theme.palette.secondary[700],
          borderColor: theme.palette.secondary[0],
          ':disabled': {
            backgroundColor: theme.palette.secondary[0],
            '& svg path': {
              stroke: theme.palette.secondary.mainDisabled,
              fill: theme.palette.secondary.mainDisabled
            }
          }
        }),
        containedError: ({ theme }) => ({
          color: theme.palette.secondary[50],
          backgroundColor: theme.palette.primary.error,
          border: theme.palette.primary.error,

          '& svg path': {
            stroke: theme.palette.secondary[50],
            fill: theme.palette.secondary[50]
          },

          ':hover': {
            backgroundColor: theme.palette.primary.errorDark,
            borderColor: theme.palette.primary.errorDark
          },

          ':disabled': {
            backgroundColor: theme.palette.primary.errorDisabled,
            color: theme.palette.secondary[50],
            borderColor: theme.palette.secondary[0]
          }
        }),
        outlinedError: ({ theme }) => ({
          border: `1px solid ${theme.palette.primary.error}`,
          background: '#FFF',
          color: theme.palette.primary.error,
          '& svg path': {
            fill: theme.palette.primary.error,
            stroke: theme.palette.primary.error
          },
          ':hover': {
            color: theme.palette.primary.errorDark,
            backgroundColor: theme.palette.secondary[200],
            borderColor: theme.palette.primary.errorDark
          },
          ':disabled': {
            color: theme.palette.primary.errorDarkDisabled,
            backgroundColor: theme.palette.secondary.opaque,
            borderColor: theme.palette.primary.errorDisabled,

            '& svg path': {
              fill: theme.palette.primary.errorDarkDisabled,
              stroke: theme.palette.primary.errorDarkDisabled
            }
          }
        }),
        textError: ({ theme }) => ({
          backgroundColor: 'transparent',
          border: 'none',
          color: theme.palette.primary.error,
          '& svg path': {
            fill: theme.palette.primary.error,
            stroke: theme.palette.primary.error
          },
          ':hover': {
            backgroundColor: theme.palette.secondary[200]
          },
          ':disabled': {
            color: theme.palette.primary.errorDarkDisabled,
            backgroundColor: theme.palette.secondary.opaque,
            '& svg path': {
              fill: theme.palette.primary.errorDarkDisabled,
              stroke: theme.palette.primary.errorDarkDisabled
            }
          }
        }),
        containedInfo: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.purpleLight,
          color: theme.palette.secondary[700],
          borderColor: theme.palette.secondary.purpleLight,

          '& svg path': {
            fill: theme.palette.secondary.main
          },

          ':hover': {
            backgroundColor: theme.palette.secondary.purpleMain,
            borderColor: theme.palette.secondary.purpleMain
          },

          ':disabled': {
            '& svg path': {
              fill: theme.palette.secondary.mainDisabled
            },
            backgroundColor: theme.palette.secondary.purpleOpaque,
            borderColor: theme.palette.secondary[0]
          }
        }),
        // @ts-expect-error - MUI types don't include custom button variants
        linkButton: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.secondary[700],
          textDecoration: 'underline',
          backgroundColor: 'transparent',
          padding: '0 !important',
          cursor: 'pointer',
          border: 'none',
          textUnderlineOffset: 2,

          ':hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline'
          }
        })
      },
      variants: [
        {
          props: { variant: 'linkButton' },
          style: {}
        }
      ]
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '72px',
          top: 'auto',
          bottom: 0,
          backgroundColor: theme.palette.secondary[900]
        })
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 'fit-content',
          '&.Mui-error': {
            color: theme.palette.primary.error
          }
        }),
        asterisk: ({ theme }) => ({
          color: theme.palette.primary.error
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          height: '49px',
          padding: '0 14px'
        },
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.secondary[300]
        }),
        root: ({ theme }) => ({
          height: '49px',
          background: theme.palette.secondary[50],
          borderRadius: '8px',
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.secondary[300]
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.error
          },
          '& > .MuiInputAdornment-positionStart': {
            marginRight: '-6px'
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            border: 'none'
          }
        }),
        multiline: {
          height: 'auto',
          padding: '10px 0'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary[700],
          '&.Mui-disabled': {
            backgroundColor: theme.palette.secondary[200],
            border: `1px solid ${theme.palette.secondary[300]}`,
            color: theme.palette.secondary[700]
          }
        }),
        input: {
          '&.Mui-disabled': {
            WebkitTextFillColor: 'unset'
          }
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        maxWidthXl: {
          '&.MuiContainer-maxWidthXl': {
            maxWidth: 1440,
            paddingLeft: '32px',
            paddingRight: '32px',
            marginTop: '48px',
            marginBottom: '48px'
          }
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',

          '& ol li:not(:first-of-type) a': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textDecoration: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            lineHeight: '20px',
            fontWeight: 500,
            color: theme.palette.secondary[700]
          },
          '& ol li:last-of-type a': {
            cursor: 'none',
            pointerEvents: 'none',
            backgroundColor: theme.palette.primary.lightest,
            padding: '4px 8px',
            borderRadius: '8px',
            fontWeight: 700
          }
        })
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginBottom: '-1px'
        },
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main
        }),
        flexContainer: {
          gap: '16px'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary[700],
          fontSize: '16px',
          fontWeight: '700',
          lineHeight: '20px',
          textTransform: 'none',
          padding: '12px',
          minWidth: '0',
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.lightest,
            color: theme.palette.secondary[900]
          }
        })
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          error: 'p'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary[900],
          padding: '0',
          fontWeight: theme.typography.fontWeightBold,
          lineHeight: '28px'
        })
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 0 32px'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
          display: 'flex',
          gap: '16px'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '24px',
          borderRadius: '12px',
          boxShadow:
            '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary[700],
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '20px'
        })
      }
    },

    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.secondary[300]
        })
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: 0,
          '&.Mui-error': {
            color: theme.palette.primary.error
          }
        })
      }
    },

    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 47,
          height: 26,
          padding: 0,

          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(21px)',
              color: theme.palette.secondary[50],
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.light,
                opacity: 1,
                border: 0
              }

              //TODO: Apply when design available
              // '&.Mui-disabled + .MuiSwitch-track': {
              //   opacity: 0.5,
              // },
            }

            //TODO: Apply when design available
            // '&.Mui-disabled .MuiSwitch-thumb': {
            //   color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
            // },
            // '&.Mui-disabled + .MuiSwitch-track': {
            //   opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
            // },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22
          },
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: theme.palette.secondary[300],
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
              duration: 500
            })
          }
        })
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          height: 'auto',
          padding: '0 0 0 5px !important'
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:after': {
            position: 'absolute',
            left: 0,
            backgroundColor: theme.palette.secondary[300],
            bottom: 0,
            right: 0,
            height: '1px',
            content: '""',
            opacity: 1,
            transition:
              'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
          },
          boxShadow: 'none',
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            '&:before': {
              opacity: 1
            }
          }
        })
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: '0'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '0 9px 0 0 !important',

          '&.Mui-checked': {
            color: theme.palette.primary.light,
            padding: '0 9px 0 0 !important'
          },

          '&.Mui-disabled': {
            color: theme.palette.secondary[400],
            cursor: 'not-allowed'
          }
        })
      }
    }
  }
})

export { molsonTheme }
